import React, { VFC, useState, useEffect, useCallback } from 'react';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { useTranslation } from 'react-i18next';
import { Box, Container, Divider, Grid, makeStyles, Theme } from '@dxb-web-ui-framework/design-system';
import { isDefined } from '@dxb-web-ui-framework/common/utils';
import { DealerRatesWithCalculatedTimer } from '../../../../../fundTransfer/src/domain/Transfer/entities/DealerRatesResponseDTO';
import { TransferRepositoryImpl } from '../../../../../fundTransfer/src/domain/Transfer/infrastructure';
import { TransferServiceImpl } from '../../../../../fundTransfer/src/domain/Transfer/usecases';
import { TransactionDocuments } from '../../../../../content-composer/src/components/Generic/TransactionDocuments';
import { ExchangeCalculateResponseBody } from '../../../../../fundTransfer/src/domain/Transfer/entities/ExchangeCalculateResponseDTO';
import { OrderChargesResponseBody } from '../../../../../fundTransfer/src/domain/Transfer/entities/OrderChargesResponseDTO';
import {
  ConsolidatedTransaction,
  ConsolidatedTransactionDetails,
} from '../../../domain/Transaction/entities/ConsolidatedTransaction';
import { AccountExtraitTransaction } from '../../../domain/Transaction/entities/AccountExtraitTransaction';
import { Mandate } from '../../../../../authorizations/src/domain/Authorization/entities';
import { TransferDocuments } from '../../../../../content-composer/src/components/Generic/TransferDocuments';
import { CustomerType } from '../../../../../userProfile/src/domain/UserInfo/entities/UserInfo';
import { useCustomerType } from '../../../../../userProfile/src/contexts/UserInfo/hooks';
import { TransactionDetailsAuthorizationButtons } from '../TransactionDetailsAuthorization';
import { DealerRateErrorDialog } from '../TransactionDetailsDealerRate';
import { TransactionInvolvedParties } from './TransactionInvolvedParties';
import { TransactionDetailsList } from './TransactionDetailsList';
import { TransactionDetailsModifications } from './TransactionDetailsModifications';

const useStyles = makeStyles<Theme>(({ breakpoints }) => ({
  leftContainer: {
    backgroundColor: '#F0F0F0',
    [breakpoints.down('sm')]: {
      backgroundColor: 'inherit',
    },
  },
}));

export interface TransactionDetailsContentProps {
  transactionDetails: ConsolidatedTransactionDetails;
  transaction?: ConsolidatedTransaction | AccountExtraitTransaction | Mandate;
  variant: 'drawer' | 'page';
  isAuthorization?: boolean;
  onToggleDrawer?: (transactionId: any) => void;
  isLoading: boolean;
  lastAuthorizer: boolean;
  needMandate: boolean;
  actionCallback?: (event: any, name: string, transactionNo: string, isActionFromDetailsDrawer: boolean) => void;
  shouldFetchDealerRates?: boolean;
  exchangeCalculateData?: ExchangeCalculateResponseBody;
  shouldShowMandateRequestRateButton?: boolean;
  shouldShowApproximateRate?: boolean;
  doesUserAwaitsForDealerRateAcceptance: boolean;
}

const getService = () => {
  const transferRepositoryImplInstance = new TransferRepositoryImpl();
  return new TransferServiceImpl(transferRepositoryImplInstance);
};

const TransactionDetailsContent: VFC<TransactionDetailsContentProps> = ({
  transactionDetails,
  transaction,
  variant,
  isLoading,
  isAuthorization,
  onToggleDrawer,
  lastAuthorizer,
  actionCallback,
  needMandate,
  shouldFetchDealerRates = false,
  shouldShowMandateRequestRateButton,
  exchangeCalculateData,
  shouldShowApproximateRate,
  doesUserAwaitsForDealerRateAcceptance,
}) => {
  const [dialerRatesTimestamp, setDialerRatesTimestamp] = useState(new Date().toISOString());
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [fundTransfersService] = useState(getService());
  const isStandalonePage = variant === 'page';
  const classes = useStyles();
  const customerType = useCustomerType();
  const isCorporateUser = customerType === CustomerType.CORPORATE;
  const { t } = useTranslation();

  const isReadyForDealerRateAcceptance =
    transactionDetails &&
    shouldFetchDealerRates &&
    Boolean(
      transactionDetails.processingStatus === '6' &&
        !!transactionDetails?.dealRefNo &&
        !!transactionDetails?.dealUsrCode,
    );

  const {
    data: dealerRatesData,
    error: dealerRatesError,
    isLoading: isLoadingDealerRates,
  } = useInvokeSWRService<DealerRatesWithCalculatedTimer>(
    fundTransfersService.GetDealerRates,
    {
      referenceNumber: transactionDetails?.dealRefNo,
      dealerUserCode: transactionDetails?.dealUsrCode,
      meta: {
        timestamp: dialerRatesTimestamp,
      },
    },
    !!isReadyForDealerRateAcceptance,
    'transferService.GetDealerRates',
  );

  const shouldFetchOrderCharges =
    !!transactionDetails &&
    isDefined(transactionDetails?.orderId) &&
    (transactionDetails?.processingStatus === '0' ||
      transactionDetails?.processingStatus === '4' ||
      transactionDetails?.processingStatus === '5');

  const {
    data: orderCharges,
    isLoading: isLoadingOrderCharges,
    error: orderChargesError,
  } = useInvokeSWRService<OrderChargesResponseBody>(
    fundTransfersService.GetOrderCharges,
    {
      orderCode: String(transactionDetails?.orderId),
    },
    shouldFetchOrderCharges,
    'transferService.GetOrderCharges',
  );

  const onRetryFetchDealerRates = useCallback(() => {
    setDialerRatesTimestamp(new Date().toISOString());
    setIsErrorDialogOpen(false);
  }, [setDialerRatesTimestamp]);

  useEffect(() => {
    if (dealerRatesError) {
      setIsErrorDialogOpen(true);
    }
  }, [dealerRatesError]);

  const shouldShowModificationsSection = transactionDetails?.type === 'O' && isCorporateUser;

  return (
    <>
      {dealerRatesError && transaction instanceof ConsolidatedTransaction && (
        <DealerRateErrorDialog
          t={t}
          isOpen={isErrorDialogOpen}
          setIsOpen={setIsErrorDialogOpen}
          onCancel={() => setIsErrorDialogOpen(false)}
          onRetry={onRetryFetchDealerRates}
        />
      )}
      <Grid container style={{ height: '100%' }}>
        <Grid item xs={isStandalonePage ? 12 : 5} className={classes.leftContainer}>
          <Container maxWidth={false} style={{ height: '100%' }}>
            <Box ml={!isStandalonePage ? 7 : 1} mr={!isStandalonePage ? 2 : 1}>
              <TransactionInvolvedParties
                isLoading={isLoading || isLoadingOrderCharges}
                isLoadingDealerRates={isLoadingDealerRates}
                isReadyForDealerRateAcceptance={isReadyForDealerRateAcceptance}
                transactionDetails={transactionDetails}
                isStandalonePage={isStandalonePage}
                dealerRates={dealerRatesData}
                needMandate={needMandate}
                fundTransfersService={fundTransfersService}
                doesUserAwaitsForDealerRateAcceptance={doesUserAwaitsForDealerRateAcceptance}
                exchangeCalculateData={exchangeCalculateData}
                shouldShowMandateRequestRateButton={shouldShowMandateRequestRateButton}
                shouldShowApproximateRate={shouldShowApproximateRate}
              />
              {shouldShowModificationsSection && (
                <TransactionDetailsModifications
                  t={t}
                  transactionDetails={transactionDetails}
                  isLoading={isLoading || isLoadingOrderCharges}
                />
              )}
              {isAuthorization && !isStandalonePage && (
                <TransactionDetailsAuthorizationButtons
                  transaction={transaction as Mandate}
                  isStandalonePage={isStandalonePage}
                  onToggleDrawer={onToggleDrawer}
                  isLoading={isLoading || isLoadingOrderCharges}
                  lastAuthorizer={lastAuthorizer}
                  actionCallback={actionCallback}
                />
              )}
            </Box>
          </Container>
        </Grid>
        {!isStandalonePage && <Divider orientation='vertical' flexItem />}
        <Grid item xs={isStandalonePage ? 12 : 2}>
          <TransactionDetailsList
            transactionDetails={transactionDetails}
            isStandalonePage={isStandalonePage}
            isLoading={isLoading || isLoadingOrderCharges}
            orderCharges={orderCharges}
            error={orderChargesError}
          />
          {Boolean(transactionDetails?.orderId) &&
            isStandalonePage &&
            transactionDetails.type === 'O' &&
            (isAuthorization || (transactionDetails.status === '5' && transactionDetails.processingStatus === '0') ? (
              <TransferDocuments
                transactionDetails={transactionDetails}
                isStandalonePage={isStandalonePage}
                shouldHideDeleteButton={false}
              />
            ) : (
              <TransactionDocuments
                transactionDetails={transactionDetails}
                isStandalonePage={isStandalonePage}
                shouldHideDeleteButton={true}
              />
            ))}
        </Grid>
        {Boolean(transactionDetails?.orderId) &&
          !isStandalonePage &&
          transactionDetails.type === 'O' &&
          (isAuthorization || (transactionDetails.status === '5' && transactionDetails.processingStatus === '0') ? (
            <TransferDocuments
              transactionDetails={transactionDetails}
              isStandalonePage={isStandalonePage}
              shouldHideDeleteButton={false}
            />
          ) : (
            <TransactionDocuments
              transactionDetails={transactionDetails}
              isStandalonePage={isStandalonePage}
              shouldHideDeleteButton={true}
            />
          ))}
      </Grid>
      {isAuthorization && isStandalonePage && (
        <TransactionDetailsAuthorizationButtons
          transaction={transaction as Mandate}
          isStandalonePage={isStandalonePage}
          isLoading={isLoading || isLoadingOrderCharges}
          onToggleDrawer={onToggleDrawer}
          actionCallback={actionCallback}
          lastAuthorizer={lastAuthorizer}
        />
      )}
    </>
  );
};

export { TransactionDetailsContent };
